import { useRef, useCallback } from 'react'
import { useTranslation } from 'next-i18next'
import { makeStyles, Container } from '@material-ui/core'
import T from './tokens'
import Reveal from './Reveal'

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '70px 0',
    background: T.color.canvas,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      padding: '40px 0',
    },
  },
  heading: {
    fontFamily: T.font.heading,
    fontSize: 32,
    fontWeight: 500,
    color: T.color.ink,
    textAlign: 'center',
    margin: '0 0 40px',
    '& span': {
      color: T.color.brand,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 22,
      margin: '0 0 24px',
    },
  },
  bentoGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1.35fr',
    gridTemplateAreas:
      '"img1 img1 txt1" "txtA txtB img2" "img3 img3 txt3" "txtC txtD img4"',
    gap: '24px',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
  },
  // "Magic Bento" treatment (React Bits vibe), reimplemented dependency-free
  // — no GSAP/ogl (ogl already broke this project's webpack-4 build).
  // Three layered effects, all pure CSS driven by two custom properties
  // (--mx/--my, the cursor position within the card) that a light
  // rAF-throttled onMouseMove handler sets — cheap per-frame cost, no
  // per-frame JS animation loop:
  //  1. `::before` — a soft radial spotlight that follows the cursor.
  //  2. `::after` — a border ring (same content-box/border-box mask-exclude
  //     technique as the shared specular-ring buttons) whose gradient is
  //     also centered on the cursor, so the border visibly brightens
  //     nearest the pointer.
  //  Both are revealed via `:hover` (cheap, no JS needed for the
  //  show/hide) and stay at opacity 0 at rest — so on touch devices
  //  (no hover) cards simply show their flat resting state, no layout
  //  change, no stray glow.
  //  3. A subtle `rotateX/rotateY` tilt toward the cursor, transform-only,
  //     applied as an inline style by the same mousemove handler (skipped
  //     entirely under prefers-reduced-motion).
  // Both pseudo-elements sit at zIndex -1 (inside the card's own isolated
  // stacking context) so they paint behind the card's real text content,
  // never obscuring it — text cards stay fully readable.
  card: {
    position: 'relative',
    isolation: 'isolate',
    overflow: 'hidden',
    borderRadius: 20,
    minHeight: 220,
    willChange: 'transform',
    transition: 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: -1,
      borderRadius: 'inherit',
      pointerEvents: 'none',
      opacity: 0,
      transition: 'opacity 0.4s ease',
      background:
        'radial-gradient(280px circle at var(--mx, 50%) var(--my, 50%), rgba(0,158,255,0.16), transparent 55%)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: -1,
      borderRadius: 'inherit',
      pointerEvents: 'none',
      padding: '1.5px',
      opacity: 0,
      transition: 'opacity 0.4s ease',
      background:
        'radial-gradient(320px circle at var(--mx, 50%) var(--my, 50%), rgba(51,201,255,0.9), rgba(0,158,255,0.2) 55%, transparent 75%)',
      WebkitMask:
        'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      maskComposite: 'exclude',
    },
    '&:hover': {
      boxShadow: '0 16px 36px rgba(0,102,204,0.16)',
    },
    '&:hover::before, &:hover::after': {
      opacity: 1,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'box-shadow 0.35s ease',
      '&:hover::before': {
        opacity: 0.6,
      },
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 'auto',
    },
  },
  imgCard: {
    background: T.color.canvasAlt,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Photographic bento images fill the rounded card edge-to-edge (cover),
    // matching the mockup, instead of the padded "contained illustration" look.
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
    },
    [theme.breakpoints.down('sm')]: {
      '& img': {
        aspectRatio: '2 / 1',
      },
    },
  },
  textCard: {
    background: T.color.surface,
    boxShadow: T.shadow.card,
    padding: '32px 28px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      padding: '24px 20px',
      textAlign: 'center',
    },
  },
  cardTitle: {
    fontFamily: T.font.heading,
    fontSize: 23,
    fontWeight: 600,
    color: T.color.brand,
    margin: '0 0 10px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
    },
  },
  cardDesc: {
    fontFamily: T.font.body,
    // Mockup (home.png @1920) bento card desc measures cap-height ~15px →
    // ~12.6px in code scale (mockup is drawn 1.1875x); set 13px.
    fontSize: 13,
    color: T.color.muted,
    lineHeight: 1.7,
    margin: 0,
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
    },
  },
  areaImg1: { gridArea: 'img1' },
  areaTxt1: { gridArea: 'txt1' },
  areaTxtA: { gridArea: 'txtA' },
  areaTxtB: { gridArea: 'txtB' },
  areaImg2: { gridArea: 'img2' },
  areaImg3: { gridArea: 'img3' },
  areaTxt3: { gridArea: 'txt3' },
  areaTxtC: { gridArea: 'txtC' },
  areaTxtD: { gridArea: 'txtD' },
  areaImg4: { gridArea: 'img4' },
}))

// Cursor-follow tilt + spotlight-position tracking for the Magic Bento
// cards. Kept as plain DOM/rAF work (no React state) so moving the mouse
// never triggers a re-render — only a rAF-batched inline-style write on
// the hovered card itself. `--mx`/`--my` (percent within the card) feed
// the spotlight + border-glow gradients in useStyles above; the small
// rotateX/rotateY tilt is applied directly as `transform` so it can
// combine with a `perspective()` on the same declaration without needing
// a separate parent-level `perspective`. Skipped entirely under
// prefers-reduced-motion (checked fresh on every pointer-enter, not
// cached, so a live OS-setting change is picked up without a reload).
const useBentoTilt = () => {
  const rafRef = useRef(null)

  const onMouseMove = useCallback((e) => {
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const el = e.currentTarget
    const rect = el.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const px = (x / rect.width) * 100
    const py = (y / rect.height) * 100
    // Small, gentle tilt — max ~4.5deg on either axis, toward the cursor.
    const rotateY = reduceMotion ? 0 : ((x / rect.width) - 0.5) * 9
    const rotateX = reduceMotion ? 0 : -((y / rect.height) - 0.5) * 9

    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      el.style.setProperty('--mx', `${px}%`)
      el.style.setProperty('--my', `${py}%`)
      if (!reduceMotion) {
        el.style.transform = `perspective(700px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-3px)`
      }
    })
  }, [])

  const onMouseLeave = useCallback((e) => {
    const el = e.currentTarget
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      el.style.transform = ''
      el.style.removeProperty('--mx')
      el.style.removeProperty('--my')
    })
  }, [])

  return { onMouseMove, onMouseLeave }
}

const FeaturesNew = () => {
  const classes = useStyles()
  const { t } = useTranslation('home')
  const bento = useBentoTilt()

  return (
    <section className={classes.root}>
      <Container maxWidth={false} style={{ maxWidth: T.container }}>
        <Reveal>
          <h2 className={classes.heading}>
            {t('features.headingBefore')} <span>SHIPPOP</span>
          </h2>
        </Reveal>

        <Reveal>
          <div className={classes.bentoGrid}>
            <div
              className={`${classes.card} ${classes.imgCard} ${classes.areaImg1}`}
              onMouseMove={bento.onMouseMove}
              onMouseLeave={bento.onMouseLeave}
            >
              <picture style={{ display: 'contents' }}>
                <source
                  srcSet="/static/img/home-new/features/feature2.webp"
                  type="image/webp"
                />
                <img
                  src="/static/img/home-new/features/feature2.png"
                  alt={t('features.alt2')}
                  width={834}
                  height={417}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>

            <div
              className={`${classes.card} ${classes.textCard} ${classes.areaTxt1}`}
              onMouseMove={bento.onMouseMove}
              onMouseLeave={bento.onMouseLeave}
            >
              <h3 className={classes.cardTitle}>{t('features.comparePrice.title')}</h3>
              <p className={classes.cardDesc}>
                {t('features.comparePrice.desc')}
              </p>
            </div>

            <div
              className={`${classes.card} ${classes.textCard} ${classes.areaTxtA}`}
              onMouseMove={bento.onMouseMove}
              onMouseLeave={bento.onMouseLeave}
            >
              <h3 className={classes.cardTitle}>{t('features.createShipment.title')}</h3>
              <p className={classes.cardDesc}>
                {t('features.createShipment.desc')}
              </p>
            </div>

            <div
              className={`${classes.card} ${classes.textCard} ${classes.areaTxtB}`}
              onMouseMove={bento.onMouseMove}
              onMouseLeave={bento.onMouseLeave}
            >
              <h3 className={classes.cardTitle}>{t('features.cod.title')}</h3>
              <p className={classes.cardDesc}>
                {t('features.cod.desc')}
              </p>
            </div>

            <div
              className={`${classes.card} ${classes.imgCard} ${classes.areaImg2}`}
              onMouseMove={bento.onMouseMove}
              onMouseLeave={bento.onMouseLeave}
            >
              <picture style={{ display: 'contents' }}>
                <source
                  srcSet="/static/img/home-new/features/feature1.webp"
                  type="image/webp"
                />
                <img
                  src="/static/img/home-new/features/feature1.png"
                  alt={t('features.alt1')}
                  width={644}
                  height={322}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>

            <div
              className={`${classes.card} ${classes.imgCard} ${classes.areaImg3}`}
              onMouseMove={bento.onMouseMove}
              onMouseLeave={bento.onMouseLeave}
            >
              <picture style={{ display: 'contents' }}>
                <source
                  srcSet="/static/img/home-new/features/feature4.webp"
                  type="image/webp"
                />
                <img
                  src="/static/img/home-new/features/feature4.png"
                  alt={t('features.alt4')}
                  width={852}
                  height={426}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>

            <div
              className={`${classes.card} ${classes.textCard} ${classes.areaTxt3}`}
              onMouseMove={bento.onMouseMove}
              onMouseLeave={bento.onMouseLeave}
            >
              <h3 className={classes.cardTitle}>{t('features.printLabel.title')}</h3>
              <p className={classes.cardDesc}>
                {t('features.printLabel.desc')}
              </p>
            </div>

            <div
              className={`${classes.card} ${classes.textCard} ${classes.areaTxtC}`}
              onMouseMove={bento.onMouseMove}
              onMouseLeave={bento.onMouseLeave}
            >
              <h3 className={classes.cardTitle}>{t('features.pickup.title')}</h3>
              <p className={classes.cardDesc}>
                {t('features.pickup.desc')}
              </p>
            </div>

            <div
              className={`${classes.card} ${classes.textCard} ${classes.areaTxtD}`}
              onMouseMove={bento.onMouseMove}
              onMouseLeave={bento.onMouseLeave}
            >
              <h3 className={classes.cardTitle}>{t('features.support.title')}</h3>
              <p className={classes.cardDesc}>
                {t('features.support.desc')}
              </p>
            </div>

            <div
              className={`${classes.card} ${classes.imgCard} ${classes.areaImg4}`}
              onMouseMove={bento.onMouseMove}
              onMouseLeave={bento.onMouseLeave}
            >
              <picture style={{ display: 'contents' }}>
                <source
                  srcSet="/static/img/home-new/features/feature3.webp"
                  type="image/webp"
                />
                <img
                  src="/static/img/home-new/features/feature3.png"
                  alt={t('features.alt3')}
                  width={667}
                  height={334}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  )
}

export default FeaturesNew
